/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      // ?option_values= only pre-selects one option; other options may default to a combination
      // that doesn't exist as a variant. Fall back to the variant ID Shopify set in the form input.
      if (!this.variant && this.productForm) {
        const idInput = this.productForm.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          const preselectedId = parseInt(idInput.value, 10);
          this.variant = this.data.product.variants.find((v) => v.id === preselectedId) || null;
        }
      }

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // Combined listings: navigate to the new product URL for a full page refresh
      const changedInput = evt.target;
      const productUrl = changedInput.dataset.productUrl;
      if (productUrl && productUrl !== '') {
        const targetPathname = new URL(productUrl, window.location.origin).pathname;
        if (!window.location.pathname.endsWith(targetPathname)) {
          const optionValueId = changedInput.dataset.optionValueId;
          const navigateUrl = optionValueId
            ? `${productUrl}?option_values=${optionValueId}`
            : productUrl;

          this.section.style.opacity = '0.5';
          this.section.style.transition = 'opacity 0.15s';
          window.location.href = navigateUrl;
          return;
        }
      }

      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    hasSimpleBundles() {
      return Boolean(document.querySelector('[data-simple-bundles-options]'));
    }

    isBundleSelectionInvalid() {
      const root = document.querySelector('[data-simple-bundles-options]');
      if (!root) return false;

      return !Array.from(root.querySelectorAll('select[name^="properties["]')).every((select) => {
        const selected = select.options[select.selectedIndex];
        return Boolean(selected?.value) && !selected.disabled;
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const buyNowBtn = this.productForm.querySelector('.buy-now-button');

      const variantAvailable = this.hasSimpleBundles()
        ? Boolean(this.variant) && !this.isBundleSelectionInvalid()
        : this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.removeAttribute('aria-disabled');
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;

      if (buyNowBtn) {
        if (variantAvailable) {
          buyNowBtn.hidden = false;
          buyNowBtn.style.display = '';
          buyNowBtn.disabled = false;
        } else {
          buyNowBtn.style.display = 'none';
          buyNowBtn.disabled = true;
        }
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.getSelectedOptions() };
      }

      const updateOptionAvailability = (optionEl, available, soldout) => {
        const el = optionEl;
        const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
        el.classList.toggle('is-unavailable', !available);

        if (optionEl.classList.contains('custom-select__option')) {
          const em = el.querySelector('em');

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = text;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
            }
          }
        } else if (!available) {
          el.nextElementSibling.title = text;
        } else {
          el.nextElementSibling.removeAttribute('title');
        }
      };

      // Flag all options as unavailable (skip pseudo variants from combined listings)
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        if (optionEl.dataset.productUrl && optionEl.dataset.productUrl !== '') return;
        updateOptionAvailability(optionEl, false, false);
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll('.js-option');
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === 'dropdown') {
                return opt.dataset.value === variant.options[selectorIndex];
              }
              return opt.value === variant.options[selectorIndex];
            });

            if (optionEl) {
              updateOptionAvailability(optionEl, variant.available, !variant.available);
            }
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      // --- Legacy price block (.product-info__price > .price) ---
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (this.price) {
        if (this.variant) {
          const formatted = this.data.formatted[this.variant.id];
          const priceCurrentEl = this.price.querySelector('.price__current');
          const priceWasEl = this.price.querySelector('.price__was');
          const unitPriceEl = this.price.querySelector('.unit-price');

          if (priceCurrentEl) priceCurrentEl.innerHTML = formatted.price;
          if (priceWasEl) priceWasEl.innerHTML = formatted.compareAtPrice || '';

          if (this.variant.unit_price_measurement) {
            const valueEl = this.price.querySelector('.unit-price__price');
            const unitEl = this.price.querySelector('.unit-price__unit');
            const value = this.variant.unit_price_measurement.reference_value;
            const unit = this.variant.unit_price_measurement.reference_unit;
            if (valueEl) valueEl.innerHTML = formatted.unitPrice;
            if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          }

          if (unitPriceEl) unitPriceEl.hidden = !this.variant.unit_price_measurement;
          this.price.classList.toggle('price--on-sale', this.variant.compare_at_price > this.variant.price);
          this.price.classList.toggle('price--sold-out', !this.variant.available);
        }

        const defaultEl = this.price.querySelector('.price__default');
        const noVariantEl = this.price.querySelector('.price__no-variant');
        if (defaultEl) defaultEl.hidden = !this.variant;
        if (noVariantEl) noVariantEl.hidden = !!this.variant;
      }

      // --- Price-with-savings block ---
      const priceWithSavings = this.section.querySelector('.price-with-savings');
      if (!priceWithSavings || !this.variant) return;

      const formatted = this.data.formatted[this.variant.id];
      if (!formatted) return;

      const priceCurrentEl = priceWithSavings.querySelector('.js-price-current');
      const priceCompareEl = priceWithSavings.querySelector('.js-price-compare');
      const priceSavingsEl = priceWithSavings.querySelector('.js-price-savings');
      const compareRow    = priceWithSavings.querySelector('.js-price-savings-row');

      if (priceCurrentEl) priceCurrentEl.innerHTML = formatted.price;

      const hasDiscount = !!formatted.compareAtPrice;
      if (priceCompareEl) priceCompareEl.innerHTML = hasDiscount ? formatted.compareAtPrice : '';
      if (priceSavingsEl) {
        if (hasDiscount && formatted.savingsPercent != null && formatted.savingsAmount != null) {
          priceSavingsEl.innerHTML = `Save ${formatted.savingsPercent}% (${formatted.savingsAmount})`;
        } else if (hasDiscount && this.variant.compare_at_price && this.variant.price) {
          // Fallback: calculate from raw variant data if savings keys are missing
          const savingsAmt = this.variant.compare_at_price - this.variant.price;
          const savingsPct = Math.round(savingsAmt / this.variant.compare_at_price * 100);
          priceSavingsEl.innerHTML = `Save ${savingsPct}%`;
        } else {
          priceSavingsEl.innerHTML = '';
        }
      }
      if (compareRow) compareRow.hidden = !hasDiscount;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
